import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { BountyDto, OwnerBountyResponseDto } from '@roadfootage/api-client'
import { useApi } from '../../composables/api'
import { readApiErrorMessage } from '../../utils/api-error'
import { bountyResponseDecisionReason } from '../../utils/limits'

export type SubmitResponseResult = { ok: true } | { ok: false; message: string }
export type DecisionResult = SubmitResponseResult

const RESPONSES_PAGE_SIZE = 50
const GENERIC_ERROR = 'Could not send your response. Please try again.'
const DECISION_ERROR = 'Could not save your decision. Please try again.'

export const useBountyResponseStore = defineStore('bountyResponse', () => {
  const { creatorBountyResponsesApi, meApi } = useApi()

  // Responder side: the bounty being answered. There is no public GET /bounties/{id}, so it lives here.
  const respondingTo = ref<BountyDto | null>(null)
  const isSubmitting = ref(false)

  // Poster side: responses to the poster's own bounties, by bounty id.
  const responsesByBounty = ref<Record<string, OwnerBountyResponseDto[]>>({})
  const loadingByBounty = ref<Record<string, boolean>>({})
  // A failed load leaves the responses unset, so reopening the section retries.
  const errorByBounty = ref<Record<string, boolean>>({})
  // The response an accept or reject is in flight for. One decision at a time.
  const decidingResponseId = ref<string | null>(null)

  function startResponse(bounty: BountyDto) {
    respondingTo.value = bounty
  }

  function cancelResponse() {
    respondingTo.value = null
  }

  async function submitResponse(incidentReportId: string): Promise<SubmitResponseResult> {
    const bountyId = respondingTo.value?.id
    if (!bountyId) return { ok: false, message: 'No bounty selected.' }
    if (isSubmitting.value) return { ok: false, message: 'Already sending.' }
    isSubmitting.value = true
    try {
      await creatorBountyResponsesApi.creatorBountyResponsesSubmitResponse({
        submitBountyResponseRequest: { bountyId, incidentReportId }
      })
      return { ok: true }
    } catch (error) {
      console.error('Failed to submit bounty response', error)
      return { ok: false, message: await readApiErrorMessage(error, GENERIC_ERROR) }
    } finally {
      isSubmitting.value = false
    }
  }

  // `silent` refreshes the list in place (after a decision) without swapping it for the spinner.
  async function fetchResponses(bountyId: string, silent = false) {
    if (loadingByBounty.value[bountyId]) return
    if (!silent) loadingByBounty.value[bountyId] = true
    errorByBounty.value[bountyId] = false
    try {
      const page = await meApi.mePaginateBountyResponsesByBountyId({
        bountyId,
        page: 1,
        pageSize: RESPONSES_PAGE_SIZE
      })
      responsesByBounty.value[bountyId] = page.items ?? []
    } catch (error) {
      console.error('Failed to fetch bounty responses', error)
      errorByBounty.value[bountyId] = true
    } finally {
      loadingByBounty.value[bountyId] = false
    }
  }

  // Runs one accept or reject. A refusal (409: already claimed or decided) carries the server's text, and either way the
  // list is reloaded so what the poster sees matches the server.
  async function decide(
    bountyId: string,
    responseId: string,
    call: () => Promise<unknown>
  ): Promise<DecisionResult> {
    if (decidingResponseId.value) return { ok: false, message: 'Already saving a decision.' }
    decidingResponseId.value = responseId
    try {
      await call()
      return { ok: true }
    } catch (error) {
      console.error('Failed to decide on bounty response', error)
      return { ok: false, message: await readApiErrorMessage(error, DECISION_ERROR) }
    } finally {
      decidingResponseId.value = null
      await fetchResponses(bountyId, true)
    }
  }

  function acceptResponse(bountyId: string, responseId: string): Promise<DecisionResult> {
    return decide(bountyId, responseId, () =>
      meApi.meAcceptBountyResponseByBountyIdAndResponseId({ bountyId, responseId })
    )
  }

  async function rejectResponse(
    bountyId: string,
    responseId: string,
    reason: string
  ): Promise<DecisionResult> {
    const trimmed = reason.trim()
    if (!trimmed) return { ok: false, message: 'Give a reason for rejecting this response.' }
    if (trimmed.length > bountyResponseDecisionReason) {
      return {
        ok: false,
        message: `Keep the reason to ${bountyResponseDecisionReason} characters or fewer.`
      }
    }
    return decide(bountyId, responseId, () =>
      meApi.meRejectBountyResponseByBountyIdAndResponseId({
        bountyId,
        responseId,
        rejectBountyResponseRequest: { reason: trimmed }
      })
    )
  }

  return {
    respondingTo,
    isSubmitting,
    responsesByBounty,
    loadingByBounty,
    errorByBounty,
    decidingResponseId,
    startResponse,
    cancelResponse,
    submitResponse,
    fetchResponses,
    acceptResponse,
    rejectResponse
  }
})
